import { useState } from "react";
import logo from "@/assets/logo.png";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, User, ShieldCheck, KeyRound } from "lucide-react";

const REGISTER_SECRET = "@K3p4l4s3k0l4";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [gateOpen, setGateOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGate = (e) => {
    e.preventDefault();
    if (secret !== REGISTER_SECRET) {
      toast.error("Password akses registrasi salah");
      return;
    }
    setGateOpen(true);
    toast.success("Akses diberikan. Silakan buat akun.");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(username, password, secret);
      toast.success("Akun berhasil dibuat");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full bg-background" data-testid="register-page">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground lg:flex">
        <div className="grain-overlay" />
        <div className="relative z-10 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
          <img src={logo} alt="Pengepul ABG" className="h-9 w-9 object-contain" />
          Pengepul ABG
        </div>
        <div className="relative z-10">
          <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
            Registrasi<br />terproteksi.
          </h1>
          <p className="mt-6 max-w-sm text-sm text-primary-foreground/70">
            Pembuatan akun hanya dapat dilakukan setelah memverifikasi password akses khusus.
          </p>
        </div>
        <div className="relative z-10 text-xs uppercase tracking-[0.2em] text-primary-foreground/50">
          Akses Terbatas
        </div>
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm fade-up">
          <div className="mb-10">
            <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Daftar</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-foreground">
              {gateOpen ? "Buat akun baru" : "Verifikasi akses"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {gateOpen
                ? "Tentukan username dan password untuk akun baru."
                : "Masukkan password akses registrasi untuk melanjutkan."}
            </p>
          </div>

          {!gateOpen ? (
            <form onSubmit={handleGate} className="space-y-5" data-testid="register-gate-form">
              <div className="space-y-2">
                <Label htmlFor="secret">Password Akses</Label>
                <div className="relative">
                  <ShieldCheck className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="secret"
                    data-testid="register-secret-input"
                    type="password"
                    className="pl-9"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    placeholder="Password akses khusus"
                    required
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="register-gate-submit-button">
                <KeyRound className="mr-2 h-4 w-4" />
                Verifikasi
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="reg-username">Username</Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="reg-username"
                    data-testid="register-username-input"
                    className="pl-9"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="username"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-password">Password</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="reg-password"
                    data-testid="register-password-input"
                    type="password"
                    className="pl-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Buat Akun
              </Button>
            </form>
          )}

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Sudah punya akun?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline" data-testid="register-to-login-link">
              Masuk
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
